/**
 * /forgot-password — Story 1c (request).
 *
 * AC #10 — anti-enumeration: the success copy is identical whether the email
 * exists or not. Don't surface "no account with that email".
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { AuthCard } from '../../components/auth/auth-card.tsx';
import { Button, Input } from '@/components/ui';
import { authApi } from '../../state/auth/api.ts';

const schema = z.object({
  email: z.string().email('enter a valid email'),
});
type FormValues = z.infer<typeof schema>;

export function ForgotPasswordPage(): JSX.Element {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const res = await authApi.requestPasswordReset(values);
      if (!res.ok) {
        setServerError('Something went wrong. Please try again.');
        return;
      }
      // Always show the neutral success state — regardless of whether the
      // address corresponds to a real account (anti-enumeration AC #10).
      setSubmitted(true);
    } catch {
      setServerError('Something went wrong. Please try again.');
    }
  });

  return (
    <AuthCard
      eyebrow="Forgot password"
      title="Reset your password."
      lede="We'll email you a link to set a new password."
      footer={
        <>
          Remembered it?{' '}
          <Link className="text-ink underline hover:text-accent" to="/sign-in">
            Sign in
          </Link>
          .
        </>
      }
    >
      {submitted ? (
        <p className="font-sans text-body text-ink" role="status">
          If that email exists, a reset link has been sent.
        </p>
      ) : (
        <form className="flex flex-col gap-5" onSubmit={onSubmit} noValidate>
          <Input label="Work email" id="fp-email" error={errors.email?.message} type="email" autoComplete="email" {...register('email')} />
          {serverError ? (
            <p className="font-sans text-small text-error" role="alert">
              {serverError}
            </p>
          ) : null}
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending…' : 'Send reset link'}
          </Button>
          {serverError ? (
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Retrying…' : 'Retry'}
            </Button>
          ) : null}
        </form>
      )}
    </AuthCard>
  );
}
